import React, { useEffect, useState } from 'react';
import { useUser } from 'util/UserContext';
import api from 'config/axiosConfig';
import AddInfluence from './AddInfluence';
import InfluenceCard from './InfluenceCard';
import AddButton from 'ui/AddButton';
import { Issue } from 'util/constants';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import { AxiosError } from 'axios';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import ScrollHideContainer from 'ui/ScrollHideContainer';

interface Props {
  issue: Issue;
}

const InfluencesSection = ({ issue }: Props) => {
  const { currentUser, setShowAuthModal } = useUser();
  const [influences, setInfluences] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const fetchInfluences = async () => {
      try {
        const response = await api.get(`/api/influences/issue/${issue.id}`);
        setInfluences(response.data);
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 404) {
            setError('404: Failed to load influences. Please try again later.');
        } else {
            setError('Failed to load influences. Please try again later.');
        }
      }
    };

    if (issue) {
      fetchInfluences();
    }
  }, [issue]);

  const handleOpenModal = () => {
    if (!currentUser) {
      setShowAuthModal(true);
    } else {
      setOpenModal(true);
    }
  };

  if (error) {
    return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find any influence for this issue.`} /> : <ServerErrorPage />;
  }

  if (!issue || !influences) {
    return (
      <LoadingSkeleton count={5} />
    );
  }

  return (
    <div className="influences-section pb-6">
      <ScrollHideContainer 
        className="z-40 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg border-b border-eoe-border-main dark:border-eoe-dark-border-main"
        hideOffset={115}
        threshold={114}
      >
        <div className="flex flex-col space-y-1 mb-1">
          <div className="flex mb-1 mx-1">
          <AddButton
          tooltipTitle="Add an influence"
          buttonText="Influence"
          onClick={handleOpenModal}
          className="mx-2 w-full"
          />
          </div>
        </div>
      </ScrollHideContainer>
      {openModal && (
        <AddInfluence
          currentIssue={issue}
          onInfluenceAdded={(newInfluence) => setInfluences([newInfluence, ...influences])}
          onClose={() => setOpenModal(false)}
          open={openModal}
          excludeIssueIds={influences.map(influence => influence.influencer_id)}
        />
      )}
      <div className="entity-divider">
        {influences.length === 0 ? (
          <p className="m-4 text-sm">Be the first to add an influence for this issue!</p>
        ) : (
          influences.map((influence) => (
            <InfluenceCard
              key={influence.id}
              influence={influence}
              showMeta={true}
              isInfluenceProfilePage={false}
              isEntitySection={true}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default InfluencesSection;